import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Save, X, AlertCircle, CheckCircle, AlertTriangle } from "lucide-react";
import { IrisZone } from "./IrisInteractiveMap";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ZoneObservationFormProps {
  zone: IrisZone;
  eyeSide: "D" | "G";
  sessionId?: string;
  existingObservation?: {
    id: string;
    observation_notes: string | null;
    severity_level: string | null;
    is_highlighted: boolean;
  };
  onSave: () => void;
  onCancel: () => void;
}

export const ZoneObservationForm: React.FC<ZoneObservationFormProps> = ({
  zone,
  eyeSide,
  sessionId,
  existingObservation,
  onSave,
  onCancel,
}) => {
  const [notes, setNotes] = useState(existingObservation?.observation_notes || "");
  const [severity, setSeverity] = useState<string>(
    existingObservation?.severity_level || "normal"
  );
  const [isHighlighted, setIsHighlighted] = useState(
    existingObservation?.is_highlighted || false
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!sessionId) {
      toast.error("Session non trouvée. Veuillez sauvegarder la session d'abord.");
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        return;
      }

      const observationData = {
        session_id: sessionId,
        user_id: user.id,
        zone_id: zone.id,
        zone_label: zone.label,
        zone_ring: zone.ring,
        eye_side: eyeSide,
        observation_notes: notes || null,
        severity_level: severity,
        is_highlighted: isHighlighted,
      };

      if (existingObservation?.id) {
        const { error } = await supabase
          .from("iridology_zone_observations")
          .update(observationData)
          .eq("id", existingObservation.id);

        if (error) throw error;
        toast.success("Observation mise à jour");
      } else {
        const { error } = await supabase
          .from("iridology_zone_observations")
          .insert(observationData);

        if (error) throw error;
        toast.success("Observation enregistrée");
      }

      onSave();
    } catch (error) {
      console.error("Error saving observation:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  const severityOptions = [
    { value: "normal", label: "Normal", icon: CheckCircle, color: "text-green-600" },
    { value: "attention", label: "À surveiller", icon: AlertTriangle, color: "text-amber-600" },
    { value: "priority", label: "Prioritaire", icon: AlertCircle, color: "text-red-600" },
  ];

  return (
    <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: zone.color }}
          />
          <span className="font-medium">{zone.label}</span>
          <Badge variant="outline" className="text-xs">
            {zone.ring === "inner" ? "Couronne interne" : "Couronne externe"}
          </Badge>
        </div>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-3">
        <div className="space-y-2">
          <Label>Niveau de priorité</Label>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {severityOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <div className="flex items-center gap-2">
                    <option.icon className={`w-4 h-4 ${option.color}`} />
                    {option.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Observations</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notez vos observations pour cette zone..."
            rows={3}
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="highlight"
            checked={isHighlighted}
            onChange={(e) => setIsHighlighted(e.target.checked)}
            className="rounded border-muted-foreground"
          />
          <Label htmlFor="highlight" className="text-sm cursor-pointer">
            Mettre en évidence sur le rapport
          </Label>
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button onClick={handleSave} disabled={isSaving} className="gap-2">
          <Save className="w-4 h-4" />
          {isSaving ? "Enregistrement..." : "Enregistrer"}
        </Button>
      </div>
    </div>
  );
};